/**
 * 船員アプリ共通のヘッダー（ノッチ／カメラ穴対応版）。
 *
 * これまでは各画面が独自にヘッダーを組み、上の余白も画面ごとにバラバラだったため、
 * スマホのカメラ穴やステータスバーに見出し・戻るボタンが潜り込んで押せない端末があった。
 *
 * 対策として、この共通ヘッダーは上下2段構成にしている。
 *   1段目 = セーフバンド: 中身を置かない「捨て余白」。ノッチに重なるのはここだけ
 *   2段目 = 実際のヘッダー（戻る／見出し／右のボタン）
 * 高さは utils/safeArea.ts が決めた --app-safe-top を使い、端末で足りなければ
 * 設定画面の「画面上の余白」で利用者自身が増やせる。
 */
import React from 'react';
import { BackButton } from './BackButton';

interface Props {
  /** 見出し（画面名） */
  title: React.ReactNode;
  /** 見出しの下の小さい行（船名など） */
  subtitle?: React.ReactNode;
  /** 戻るボタンを出すか（ホーム等、戻り先が無い画面は false） */
  back?: boolean;
  /** 右端に置くもの（件数バッジ・更新ボタン等） */
  right?: React.ReactNode;
  /** ヘッダーの中の下段に足すもの（月の切替など） */
  children?: React.ReactNode;
  /** 背景色（既定は白） */
  background?: string;
  /** 見出しの文字色 */
  color?: string;
  /** 小さい行の文字色（暗い背景の画面用） */
  subColor?: string;
  /** 左右の余白 */
  padX?: number;
  style?: React.CSSProperties;
}

export const AppHeader: React.FC<Props> = ({
  title, subtitle, back = true, right, children,
  background = '#fff', color = '#0f172a', subColor = '#64748b', padX = 16, style,
}) => (
  <div
    style={{
      background, flexShrink: 0, borderBottom: '1px solid #e2e8f0',
      boxShadow: '0 1px 3px rgba(15,23,42,0.06)', ...style,
    }}
  >
    {/* セーフバンド: ノッチ／カメラ穴に重ねてよい空き。中身は絶対に置かない */}
    <div className="app-safe-band" aria-hidden="true" />

    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: `0 ${padX}px 10px` }}>
      {back && <BackButton size={38} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 20, fontWeight: 900, color, lineHeight: 1.25,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{title}</div>
        {subtitle != null && subtitle !== '' && (
          <div style={{
            fontSize: 12.5, color: subColor, marginTop: 1, lineHeight: 1.3,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{subtitle}</div>
        )}
      </div>
      {right}
    </div>

    {children != null && <div style={{ padding: `0 ${padX}px 10px` }}>{children}</div>}
  </div>
);
